import { Types as AmplitudeTypes } from '@amplitude/analytics-browser';
import { ENV } from '@user-portal/helpers';
import { logger } from '@user-portal/logger';

import { SYSTEMS_NAMES } from './enums';

// interfaces
import { IInitSystems, SendAllOptions, SendOptions, ISystemParams } from './Interfaces/Systems';
import { ITrackOptions, ITrackNew, PurchaseData, EventDataTrack, GTMEvent } from './Interfaces/Events';
import AwsAnalytic from './Systems/Amazon';

// helpers
import Utils from './helpers/Utils';
import AmplitudeAnalytic from './Systems/Amplitude';
import { SYSTEMS } from './constants';
import { AnalyticsType, GoogleTagManagerType } from './types';
import SystemsRegistry from './SystemsRegistry';

const SYSTEMS_WITH_USER_PROPERTIES = [SYSTEMS_NAMES.amplitude];

interface SendFunction {
  (system: AnalyticsType): void;
}

class Analytic {
  private state: {
    activeSystems: AnalyticsType[];
    isInit: boolean;
    gtmInstance: GoogleTagManagerType | null;
    allInitialized: boolean;
    defaulTrackOptions: {
      isPrefix: boolean;
      data?: object;
      selectedSystems: SYSTEMS_NAMES[];
    };
  };

  private activeSystems = [];
  private skipEnvironments: Set<ENV>;

  private static readonly SKIP_INITIALIZATION_ENVIRONMENTS = new Set([]);

  constructor() {
    this.state = {
      activeSystems: [],
      isInit: false,
      gtmInstance: null,
      defaulTrackOptions: {
        isPrefix: true,
        selectedSystems: this.activeSystems,
      },
      allInitialized: false,
    };

    this.skipEnvironments = Analytic.SKIP_INITIALIZATION_ENVIRONMENTS;

    SystemsRegistry.getInstance().registerAnalyticsProvider({
      checkIsAlreadyInitiated: (params: { name: string }) => this.checkIsAlreadyInitiated(params),
      getDefaultAmazonAnalyticPurchaseData: async () => {
        const data = await this.getDefaultAmazonAnalyticPurchaseData();
        return data;
      },
    });
  }

  private shouldSkipInitialization(envMode?: string): boolean {
    return this.skipEnvironments.has(envMode as ENV);
  }

  private logSystemSkipped(systemName: string, reason: string): void {
    logger.info(`%cSkipping init - ${systemName} (${reason})`);
  }

  initSystems(listOfSystems: IInitSystems) {
    const options = { event: 'Init system' };

    listOfSystems.activeSystems.forEach((system: ISystemParams) => {
      const systemEnvMode = system.config?.envMode;
      if (this.shouldSkipInitialization(systemEnvMode)) {
        this.logSystemSkipped(system.name, 'nonprod environment');
        return;
      }

      this.send(system, options, () => {
        const checkSystemsStatus: boolean = this.checkIsAlreadyInitiated(system);
        if (!checkSystemsStatus) {
          const analyticInstance = new SYSTEMS[system.name]();

          analyticInstance.init(
            Utils.cleanObject({
              id: system.id,
              config: system?.config,
              email: system?.email,
            })
          );

          if (analyticInstance.getName() === 'gtm') {
            this.state.gtmInstance = analyticInstance as GoogleTagManagerType;
          } else {
            // exclude GTM from activeSystems
            this.state.activeSystems.push(analyticInstance);
          }

          logger.info(`%cinit - ${analyticInstance.getName()}`);
        }
      });
    });

    const expectedSystemsCount = listOfSystems.activeSystems.filter((system) => {
      const systemEnvMode = system.config?.envMode;
      return !this.shouldSkipInitialization(systemEnvMode);
    }).length;

    if (expectedSystemsCount === this.state.activeSystems.length) {
      logger.info('All systems are initialized');
      this.state.allInitialized = true;
    }
  }

  send(
    system: ISystemParams | AnalyticsType,
    options: SendOptions,
    callback: (system?: ISystemParams | AnalyticsType) => void
  ): void {
    try {
      callback(system);
    } catch (error) {
      const defaultOptions = {
        systemName: system.name,
        error: error,
      };
      Utils.errorConsoleGroup({ ...defaultOptions, ...options });
    }
  }

  createCustomSystems(customSystems: string[]) {
    return customSystems.map((el: string) => {
      return this.state.activeSystems.find((item: AnalyticsType) => item.getName() === el);
    });
  }

  sendAll(options: SendAllOptions, callback: SendFunction) {
    const customSystems = options?.customSystems?.length && this.createCustomSystems(options.customSystems);
    const activeSystems = this.state.activeSystems;

    const systems = customSystems || activeSystems;

    systems.forEach((system?: AnalyticsType) => {
      if (system) {
        // @ts-ignore
        this.send(system, options, callback);
      }
    });
  }

  getSystemsState() {
    return this.state.allInitialized;
  }

  purchase(data: PurchaseData, trackOptions: ITrackOptions = this.state.defaulTrackOptions) {
    const options = {
      event: 'Purchase',
      customSystems: trackOptions.selectedSystems,
    };
    this.sendAll(options, (system: AnalyticsType) => {
      // this object copy was done to do not mutate original data, because of f.e. Pixel purchase event removes 'price'
      // field from the data object and other analytics purchase events won't have 'price' field also.
      const analyticData = { ...data };
      system.purchase('purchase', analyticData);
    });
  }

  purchaseNew(data: PurchaseData, trackOptions: ITrackOptions = this.state.defaulTrackOptions) {
    const options = {
      event: 'Purchase',
      customSystems: trackOptions.selectedSystems,
    };

    this.sendAll(options, (system: AnalyticsType) => {
      // this object copy was done to do not mutate original data, because of f.e. Pixel purchase event removes 'price'
      // field from the data object and other analytics purchase events won't have 'price' field also.
      const analyticData = { ...data };
      system.purchaseNew({ eventName: 'purchase', data: analyticData });
    });
  }

  setUserProperties(data: Record<string, AmplitudeTypes.ValidPropertyType | null | undefined>) {
    this.sendAll(
      {
        event: 'setUserProperties',
        customSystems: SYSTEMS_WITH_USER_PROPERTIES,
      },
      (system: AnalyticsType) => {
        !(system instanceof AmplitudeAnalytic) || system?.setUserProperties(data);
      }
    );
  }

  clearUserProperties() {
    this.sendAll(
      {
        event: 'clearUserProperties',
        customSystems: SYSTEMS_WITH_USER_PROPERTIES,
      },
      (system: AnalyticsType) => {
        !(system instanceof AmplitudeAnalytic) || system.clearUserProperties();
      }
    );
  }

  customData(event: string, data: EventDataTrack | null = null) {
    const options = {
      event: event,
      data: Utils.normalizeEventLabelFormat(data),
    };
    this.sendAll(options, (system: AnalyticsType) => {
      system.track(Utils.normalizeLabel(event), Utils.normalizeEventLabelFormat(data));
    });
  }

  trackNew({ eventData, actionData = null, data = null }: ITrackNew) {
    const event = typeof eventData === 'object' ? `${eventData.screenId}__${eventData.event}` : eventData;

    let action: string | null = null;

    if (typeof actionData === 'object') {
      if (actionData?.elementEvent && actionData?.actionEvent) {
        action = `${actionData.elementEvent}__${actionData.actionEvent}`;
      }
    }

    // Fallback logic to use custom eventAction
    if (typeof actionData === 'string') {
      action = actionData;
    }

    const options = {
      event: event,
      data: Utils.normalizeEventLabelFormat(data),
    };
    this.sendAll(options, (system: AnalyticsType) => {
      system.trackNew({
        eventName: Utils.normalizeLabel(event),
        eventAction: action ? Utils.normalizeLabel(action) : null,
        eventData: Utils.normalizeEventLabelFormat(data),
      });
    });
  }

  track(event: string, trackOptions: ITrackOptions = this.state.defaulTrackOptions) {
    const options = {
      event: event,
      customSystems: trackOptions.selectedSystems,
    };
    this.sendAll(options, (system: AnalyticsType) => {
      const eventName = Utils.addPrefix(Utils.normalizeLabel(event), trackOptions.isPrefix);
      system.track(eventName);
    });
  }

  trackClick(event: string) {
    const options = { event: event };
    this.sendAll(options, (system: AnalyticsType) => {
      system.track(event);
    });
  }

  trackGTM({ name, type, data }: GTMEvent) {
    if (!this.state.gtmInstance) {
      logger.warn('GTM is not active');

      return;
    }

    this.state.gtmInstance.trackGTM({
      name: Utils.normalizeLabel(name),
      data: Utils.normalizeEventLabelFormat(data),
      type,
    });
  }

  checkIsAlreadyInitiated(checkSystem: AnalyticsType | { name: string }): boolean {
    let status = false;
    this.state.activeSystems.forEach((system: AnalyticsType) => {
      if (system.getName() === checkSystem.name) {
        logger.warn(`Already inited system - ${checkSystem.name}`);
        status = true;
      }
    });

    return status;
  }

  getDefaultAmazonAnalyticPurchaseData() {
    const awsSystem = this.state.activeSystems.find((item: AnalyticsType) => item.name === 'amazon');

    return awsSystem instanceof AwsAnalytic ? awsSystem?.getDefaultRecordData() : null;
  }
}

export default Analytic;
